/**
 * Хранилище очереди синхронизации.
 *
 * Очередь переживает перезагрузку страницы: результат тренировки, набранной
 * офлайн, не должен теряться из-за закрытой вкладки (ТЗ FR-SY-01).
 */

import type { QueuedOperation, SyncQueueRepository } from '@type2x/feature-sync';

import {
  SYNC_QUEUE_STORE,
  clearStore,
  listRecords,
  openDatabase,
  writeRecord,
} from './indexeddb.js';

/** Репозиторий очереди поверх IndexedDB. */
export function createIndexedDbSyncQueueRepository(dbName?: string): SyncQueueRepository {
  const connection = openDatabase(dbName);

  return {
    async load() {
      const db = await connection;
      const records = await listRecords<QueuedOperation>(db, SYNC_QUEUE_STORE);
      // Порядок постановки важен: сессии приходят на сервер так же, как их
      // прошёл пользователь.
      return [...records].sort((a, b) => a.queuedAt.localeCompare(b.queuedAt));
    },

    async save(operations) {
      const db = await connection;
      // Очередь небольшая, а перезапись целиком избавляет от рассинхронизации
      // между содержимым памяти и хранилищем.
      await clearStore(db, SYNC_QUEUE_STORE);
      for (const operation of operations) {
        await writeRecord(db, SYNC_QUEUE_STORE, operation);
      }
    },
  };
}

/**
 * Репозиторий в памяти.
 *
 * Используется в тестах и когда IndexedDB недоступна: тренировка обязана
 * работать, теряя только сохранение очереди между перезагрузками.
 */
export function createInMemorySyncQueueRepository(): SyncQueueRepository {
  let stored: QueuedOperation[] = [];

  return {
    load: () => Promise.resolve([...stored]),
    save: (operations) => {
      stored = [...operations];
      return Promise.resolve();
    },
  };
}
